import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import isEqual from 'lodash.isequal';

import { AppState } from 'services/types';
import {
    VirtualizedTableColumnKey,
    VirtualizedTableColumnWidth,
    VirtualizedTableColumns,
    VirtualizedTableItem
} from 'services/virtualizedTable/types';
import { setColumnWidth } from 'services/columnWidths/actions';
import {
    requestedCreateTimesheetHoliday,
    requestedUpdateTimesheetHoliday
} from 'services/timesheetHolidays/actions';
import VirtualizedTable from 'components/VirtualizedTable';

import {
    selectTimesheetHolidaysTableColumns,
    makeSelectTimesheetHolidayItems,
    selectIsWritable
} from './selectors';

interface OwnProps {
    tableId: string;
}

interface ConnectedProps {
    items: VirtualizedTableItem[];
    columns: VirtualizedTableColumns;
    isWritable: ReturnType<typeof selectIsWritable>;
}

interface DispatchProps {
    setColumnWidth: typeof setColumnWidth;
    requestedCreateTimesheetHoliday: typeof requestedCreateTimesheetHoliday;
    requestedUpdateTimesheetHoliday: typeof requestedUpdateTimesheetHoliday;
}

type Props = OwnProps & ConnectedProps & DispatchProps;

export class TimesheetHolidaysVirtualizedTable extends React.Component<Props> {
    shouldComponentUpdate(nextProps: Props): boolean {
        return !isEqual(this.props, nextProps);
    }

    handleColumnResized = (
        columnKey: VirtualizedTableColumnKey,
        columnWidth: VirtualizedTableColumnWidth
    ): void => {
        const { tableId, ...props } = this.props;
        props.setColumnWidth(tableId, columnKey, columnWidth);
    };

    handleCreate = (item: VirtualizedTableItem): void => {
        this.props.requestedCreateTimesheetHoliday(item);
    };

    handleItemUpdate = (item: VirtualizedTableItem): void => {
        const { id, entityIds } = item;

        this.props.requestedUpdateTimesheetHoliday(id, { entityIds });
    };

    renderVirtualizedTable = ({ height, width }: Size): React.ReactElement => {
        const { tableId, items, columns, isWritable } = this.props;

        return (
            <VirtualizedTable
                id={tableId}
                height={height}
                width={width}
                columns={columns}
                items={items}
                onColumnResized={this.handleColumnResized}
                label="Holiday"
                labelPlural="Holidays"
                editable={isWritable}
                enableCreating={isWritable}
                enableItemEditing={isWritable}
                onCreate={this.handleCreate}
                onItemUpdate={this.handleItemUpdate}
            />
        );
    };

    render(): React.ReactElement {
        return (
            <AutoSizer>
                {(size: Size): React.ReactElement =>
                    this.renderVirtualizedTable(size)
                }
            </AutoSizer>
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    items: makeSelectTimesheetHolidayItems(),
    columns: selectTimesheetHolidaysTableColumns,
    isWritable: selectIsWritable
});

export default connect(mapStateToProps, {
    setColumnWidth,
    requestedCreateTimesheetHoliday,
    requestedUpdateTimesheetHoliday
})(TimesheetHolidaysVirtualizedTable);
